import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Users, Award, Clock, CheckCircle, Target, Eye, Heart } from "lucide-react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const el = ref.current;
    if (el) {
      el.querySelectorAll(".animate-on-scroll").forEach((c) => observer.observe(c));
      if (el.classList.contains("animate-on-scroll")) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return ref;
}

const values = [
  { icon: Shield, title: "Reliability", desc: "We deliver systems you can trust, built with the highest quality components and expert craftsmanship." },
  { icon: Eye, title: "Innovation", desc: "Staying ahead with the latest technology to provide cutting-edge security and smart solutions." },
  { icon: Heart, title: "Customer First", desc: "Your safety and satisfaction drive everything we do, from consultation to ongoing support." },
  { icon: Target, title: "Excellence", desc: "Attention to detail in every installation, configuration, and service interaction." },
];

const stats = [
  { value: "500+", label: "Projects Completed" },
  { value: "7+", label: "Years Experience" },
  { value: "200+", label: "Happy Clients" },
  { value: "24/7", label: "Support Available" },
];

export default function About() {
  const ref = useInView();

  return (
    <div ref={ref}>
      {/* Hero */}
      <section className="pt-32 pb-20 section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">About K-Tec</p>
            <h1 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Your Trusted Partner
              <br />in <span className="gradient-text">Security & Technology</span>
            </h1>
            <p className="animate-on-scroll stagger-2 text-lg text-brand-muted leading-relaxed">
              Professional CCTV, Smart Home, Alarm Systems, Starlink Internet and Networking Solutions across Sri Lanka.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="animate-on-scroll font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                Who We Are
              </h2>
              <div className="animate-on-scroll stagger-1 space-y-4 text-brand-muted leading-relaxed">
                <p>
                  K-Tec.lk is a leading security and technology solutions provider based in Kandy, Sri Lanka.
                  We specialize in designing, installing, and maintaining professional-grade security systems,
                  smart home automation, and enterprise networking infrastructure.
                </p>
                <p>
                  With over 7 years of experience, we have completed 500+ projects across Sri Lanka,
                  serving both residential and commercial clients. Our team of certified technicians ensures
                  every installation meets the highest standards of quality and reliability.
                </p>
                <p>
                  We partner with world-renowned brands including Dahua, Hikvision, EZVIZ, TP-Link,
                  Ubiquiti, Ruijie, and Starlink to deliver the best solutions at competitive prices.
                </p>
              </div>
            </div>
            <div className="animate-on-scroll stagger-2">
              <div className="bg-brand-gray rounded-3xl p-8 aspect-[4/3] flex items-center justify-center">
                <div className="text-center">
                  <Award size={48} className="text-brand-orange/30 mx-auto mb-4" />
                  <p className="text-brand-muted text-sm">Trusted Excellence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-brand-orange">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className={`animate-on-scroll stagger-${i + 1} text-center text-white`}>
                <p className="text-4xl sm:text-5xl font-bold mb-2">{s.value}</p>
                <p className="text-white/80 text-sm font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Our Values</p>
            <h2 className="animate-on-scroll stagger-1 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              What Drives Us
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <div key={i} className={`animate-on-scroll stagger-${i + 1} text-center`}>                
                <div className="w-16 h-16 bg-brand-orange/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <v.icon size={28} className="text-brand-orange" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-brand-gray">
        <div className="container-custom text-center">
          <h2 className="animate-on-scroll font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">
            Work With Us
          </h2>
          <p className="animate-on-scroll stagger-1 text-lg text-brand-muted mb-10 max-w-2xl mx-auto">
            Experience the K-Tec difference. Contact us for a free consultation and discover how we can enhance your security and technology infrastructure.
          </p>
          <div className="animate-on-scroll stagger-2 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Get Free Consultation
              <ArrowRight size={18} className="ml-2" />
            </Link>
            <Link to="/services" className="btn-outline">
              Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
